import { useState } from 'react';
import { toast } from 'sonner';
import type { ESignPackage, Participant, SyncLogEntry } from '../types/docmagic';
import { nowIso } from '../utils/datetime';

export type BusyAction = 'sync' | 'pdf';

function makeLog(entry: Omit<SyncLogEntry, 'id' | 'at'>): SyncLogEntry {
  return { ...entry, id: `log-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, at: nowIso() };
}

export function usePackages(initial: ESignPackage[]) {
  const [items, setItems] = useState<ESignPackage[]>(initial);
  const [selectedId, setSelectedId] = useState<string>(initial[0].id);
  const [busy, setBusy] = useState<Record<string, BusyAction | undefined>>({});

  const selected = items.find((p) => p.id === selectedId) ?? items[0];

  const run = (
    id: string,
    action: BusyAction,
    delay: number,
    apply: (p: ESignPackage) => ESignPackage,
    message: string,
  ) => {
    setBusy((b) => ({ ...b, [id]: action }));
    window.setTimeout(() => {
      setItems((prev) => prev.map((p) => (p.id === id ? apply(p) : p)));
      setBusy((b) => ({ ...b, [id]: undefined }));
      toast.success(message);
    }, delay);
  };

  const syncPackage = (id: string) =>
    run(
      id,
      'sync',
      1400,
      (p) => ({
        ...p,
        syncStatus: 'OK',
        syncError: undefined,
        lastSyncedAt: nowIso(),
        logs: [
          makeLog({
            direction: 'Outbound',
            method: 'GET',
            endpoint: `/v2/packages/${p.docMagicPackageId}/participants`,
            status: 200,
            note: `Participant activity refreshed (${p.participants.length} participant${p.participants.length === 1 ? '' : 's'})`,
          }),
          ...p.logs,
        ],
      }),
      'Package synced with DocMagic',
    );

  const retryPdf = (id: string) =>
    run(
      id,
      'pdf',
      1800,
      (p) => {
        const fileName = `Hernandez_${p.type.replace(/[^A-Za-z]/g, '')}_Signed_${p.docMagicPackageId}.pdf`;
        return {
          ...p,
          pdfStatus: 'Stored',
          pdfError: undefined,
          pdfFileName: fileName,
          receivedDocuments: [
            { id: `r-${Date.now()}`, name: fileName, from: 'DocMagic', receivedAt: nowIso(), sizeKb: 1942 },
            ...p.receivedDocuments,
          ],
          versions: [
            ...p.versions,
            {
              id: `v-${Date.now()}`,
              version: p.versions.length + 1,
              createdAt: nowIso(),
              createdBy: 'DocMagic Integration',
              note: 'Signed PDF retrieved on retry and attached to loan record',
            },
          ],
          logs: [
            makeLog({ direction: 'Inbound', method: 'RESPONSE', endpoint: '/webhooks/docmagic/package-event', status: 201, note: 'Processing complete — 201 Created returned to DocMagic' }),
            makeLog({ direction: 'Outbound', method: 'POST', endpoint: '/services/data/v60.0/sobjects/ContentVersion', status: 201, note: `Signed PDF attached to Loan ${p.rcmLoanNumber ?? ''}`.trim() }),
            makeLog({ direction: 'Outbound', method: 'GET', endpoint: `/v2/packages/${p.docMagicPackageId}/signed-pdf`, status: 200, note: 'Signed package retrieved (1.9 MB)' }),
            ...p.logs,
          ],
        };
      },
      'Signed PDF retrieved and attached to the loan',
    );

  const sendEmail = (participant: Participant) =>
    toast.success(`Reminder email sent to ${participant.name}`, { description: participant.email });

  const openSigning = (participant: Participant) =>
    toast(`Opening DocMagic signing session`, { description: `${participant.name} · ${participant.referenceId}` });

  return { items, selected, selectedId, setSelectedId, busy, syncPackage, retryPdf, sendEmail, openSigning };
}
